import "./Breadcrumb.scss";

import AppRoutes from "AppRoutes";
import classNames from "classnames";
import { IconHouse } from "components/Icons/IconHouse";
import { getBoardTitle, getPreferredName } from "helpers";
import { useBoard, useCustomParams, useMeeting, useTranslations } from "providers";
import React, { ReactNode, useMemo } from "react";
import { NavLink } from "react-router";

import { BreadcrumbMeeting } from "./BreadcrumbMeeting";
import { BreadCrumbToggleBoardSettings } from "./BreadCrumbToggleBoardSettings";

interface BreadcrumbItem {
  to: string;
  name: string | ReactNode;
  code?: string;
  tooltip?: string;
  isActive?: boolean;
  component?: React.FC;
}

export const Breadcrumb = () => {
  const i18n = useTranslations();
  const customParams = useCustomParams();
  const { board } = useBoard();
  const { meeting, meetingId } = useMeeting();

  const items = useMemo(() => {
    const items: (BreadcrumbItem | React.FC)[] = [];

    items.push({
      to: AppRoutes.Home,
      code: "h",
      name: <IconHouse />,
      isActive: false,
    });

    if (customParams.isBoards || board) {
      items.push({
        to: AppRoutes.toBoards(),
        tooltip: i18n.Boards,
        name: i18n.Boards,
        isActive: true,
      });
    }

    if (board) {
      const name = getBoardTitle(i18n, getPreferredName(board));
      items.push({
        to: AppRoutes.toBoardMeetings(board),
        code: "bd",
        tooltip: i18n.Board,
        name,
      });
    }

    if (meetingId && meeting) {
      items.push(BreadcrumbMeeting);
    }

    return items;
  }, [i18n, board, meeting, meetingId, customParams]);

  return (
    <div id="breadcrumbs-container" className="breadcrumb-container">
      <div className="d-flex flex-grow-1">
        {items.map((item, index) => {
          const idToUse = `bi-${index}`;

          function renderComponent(item: React.FC): React.ReactNode {
            const Component = item;
            return <Component />;
          }

          function renderBreadcrumbItem(item: BreadcrumbItem): React.ReactNode {
            const anchorClass = classNames(item.isActive ? "" : "date-range-inactive");
            const textClass = classNames("text", "global-tooltip-trigger");
            const truncationClassName = index !== 0 ? " text-truncate" : "";

            return (
              <div className={classNames("breadcrumb-item", truncationClassName)}>
                <NavLink to={item.to} className={anchorClass}>
                  <span className={textClass} title={item.tooltip}>
                    {item.name}
                  </span>
                </NavLink>
              </div>
            );
          }

          return (
            <React.Fragment key={idToUse}>
              {index !== 0 && <span className="divider">&gt;</span>}
              {typeof item === "function" ? renderComponent(item) : renderBreadcrumbItem(item)}
            </React.Fragment>
          );
        })}
      </div>

      <BreadCrumbToggleBoardSettings />
    </div>
  );
};
